import { Editor, Extensions, useEditor } from "@tiptap/vue-3";
import { Node } from "prosemirror-model";
import type { JSONContent } from "@tiptap/core";
import AiTextResults from "@/components/mobile/editor-ext/ai-text-results";
import { Placeholder } from "@tiptap/extension-placeholder";
import { Ref } from "vue/dist/vue";
import { useRenderExtension } from "@/stores/story";
import { EditorOptions } from "@tiptap/core";
import i18n from "@/locales";

const getPrevContext = (editor: Editor, maxLength = 200) => {
  const { from } = editor.state.selection;
  const fullPrevText = editor.state.doc.textBetween(0, from, "\n", "\n");
  return fullPrevText.slice(
    Math.max(0, fullPrevText.length - maxLength),
    fullPrevText.length
  );
};

const getNextContext = (editor: Editor, maxLength = 200) => {
  const { to } = editor.state.selection;
  const fullNextText = editor.state.doc.textBetween(
    to,
    editor.state.doc.nodeSize - 2,
    "\n",
    "\n"
  );
  return fullNextText.slice(0, Math.min(maxLength, fullNextText.length));
};

function getCurrentNode(editor: Editor): Node {
  const { from } = editor.state.selection;
  const $from = editor.state.tr.doc.resolve(from);
  return $from.node($from.depth);
}

function removeAiTextNode(editor: Editor): void {
  // const node = getCurrentNode(editor);
  const remove = (n: Node, pos: number) => {
    if (n.type.name === "aiTextResults") {
      editor
        .chain()
        .setMeta("addToHistory", false)
        .deleteRange({
          from: pos,
          to: pos + n.nodeSize,
        })
        .run();
      return false;
    }
    return true;
  };
  // remove(node, editor.state.selection.from);
  editor.state.doc.descendants((node, pos) => {
    return remove(node, pos);
  });
}

function removeAiTextNodeFromJson(json: JSONContent) {
  json.content = json.content?.filter((item) => item.type !== "aiTextResults");
  json.content?.forEach(removeAiTextNodeFromJson);
}

function scrollToFrom(editor: Editor, from?: number): void {
  const { top } = editor.view.coordsAtPos(from ?? editor.state.selection.from);
  console.log(editor.view.coordsAtPos(from ?? editor.state.selection.from));
  window.scrollTo({
    top: top - 200,
    behavior: "smooth",
  });
}

function getJsonFromPlainText(text: string) {
  const paragraphs: JSONContent = text
    .split("\n")
    .filter((item) => item)
    .map((line) => ({
      type: "paragraph",
      content: [{ type: "text", text: line, content: line }],
    }));
  return { type: "doc", content: paragraphs } as JSONContent;
}

function getFrom(editor: Editor): number {
  return editor.state.selection.from;
}

const sliceByParagraph = (json: JSONContent, reserveParagraphCount = 1) => {
  const newContent = [];
  let currentParagraphCount = 0;
  for (const item of json.content!) {
    if (item.type === "paragraph") {
      currentParagraphCount++;
    }
    if (currentParagraphCount > reserveParagraphCount) {
      break;
    }
    newContent.push(item);
  }
  return {
    type: "doc",
    content: newContent,
  };
};

const useStoryEditor = (options: Partial<EditorOptions> = {}) => {
  let extensions: Extensions = useRenderExtension();
  if (options.editable !== false) {
    extensions = [
      ...extensions,
      AiTextResults,
      Placeholder.configure({
        placeholder: i18n.global.t("story.tellYourStory"),
      }),
    ];
  }
  return useEditor({
    extensions,
    editorProps: {
      attributes: {
        class: "prose prose-base lg:prose-lg focus:outline-none max-w-full",
      },
    },
    ...options,
  }) as Ref<Editor>;
};

export default {
  getPrevContext,
  getNextContext,
  getCurrentNode,
  removeAiTextNode,
  scrollToFrom,
  getFrom,
  removeAiTextNodeFromJson,
  getJsonFromPlainText,
  sliceByParagraph,
  useStoryEditor,
};
